import React, { useState, useEffect } from 'react';
import { Typography, Card, Space, Button, Tag, Tabs, Breadcrumb, Modal, Form, Input, TimePicker, Spin, Divider, AutoComplete, Select } from 'antd';
import { InfoCircleOutlined, ReloadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined, HolderOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { scheduleAPI, tokenManager } from '../services/api';
import { DndContext, closestCenter, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import greetingService from '../services/greeting';
import { GreetingItem } from '../types/greeting';
import { getMessage, showConfirmModal } from '../components/PopupModal';
import ContentItemManagement from '../components/ContentItemManagement';
import contentItemAPI from '../services/contentItemAPI';

dayjs.extend(isBetween);

const { Title, Text } = Typography;

interface ScheduleContent {
  id?: number;
  contentItemId: number;
  contentItem?: {
    id: number;
    contentType: string;
    contentTitle: string;
    description?: string;
  };
  displayOrder: number;
  duration: string;
}

interface Schedule {
  id: number;
  scheduleType: string;
  timeSlotName: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  contents: ScheduleContent[];
  createdBy?: number;
  createdAt?: string;
  updatedAt?: string;
}

// 드래그 가능한 콘텐츠 아이템 컴포넌트
interface DraggableContentItemProps {
  id: string;
  children: React.ReactNode;
}

const DraggableContentItem: React.FC<DraggableContentItemProps> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Space style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
        <div {...attributes} {...listeners} style={{ cursor: 'move', display: 'flex', alignItems: 'center' }}>
          <HolderOutlined style={{ color: '#8c8c8c' }} />
        </div>
        {children}
      </Space>
    </div>
  );
};

const ScheduleManagement: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [activeTab, setActiveTab] = useState<string>('평일');
  const [refreshKey, setRefreshKey] = useState(0);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [scheduleTypes, setScheduleTypes] = useState<string[]>(['평일', '주말', '공휴일']);
  const [selectedRealtimeType, setSelectedRealtimeType] = useState<string>('평일');
  const [realtimeSchedules, setRealtimeSchedules] = useState<Schedule[]>([]);
  const [greetings, setGreetings] = useState<GreetingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);
  const [contentItems, setContentItems] = useState<any[]>([]);
  const [form] = Form.useForm();

  const user = tokenManager.getUser();
  const isDeveloperAdmin = user?.role === 'development_admin';

  // 드래그 앤 드롭 센서 설정 (콘텐츠 아이템용)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // ContentItem 목록 로드
  const loadContentItems = async () => {
    try {
      const response = await contentItemAPI.getContentItems({ isActive: true, limit: 1000 });
      setContentItems(response.data || []);
    } catch (error: any) {
      console.error('ContentItem 목록 로드 실패:', error);
      setContentItems([]);
    }
  };

  // 스케줄 목록 로드
  const loadSchedules = async (scheduleType?: string) => {
    try {
      setLoading(true);
      const response = await scheduleAPI.getSchedules(scheduleType || activeTab);
      setSchedules(response.data || []);
    } catch (error: any) {
      getMessage().error(error.response?.data?.message || '스케줄 목록을 불러오는데 실패했습니다.');
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  };

  // 전체 스케줄 로드 및 타입 목록 추출
  const loadAllSchedules = async () => {
    try {
      const response = await scheduleAPI.getSchedules();
      const allData = response.data || [];

      // 고유한 스케줄 타입 추출
      const uniqueTypes: string[] = Array.from(new Set(allData.map((s: Schedule) => s.scheduleType)));

      if (uniqueTypes.length > 0) {
        // 서버에서 저장된 순서 불러오기
        try {
          const orderResponse = await scheduleAPI.getScheduleTypesOrder();
          const savedTypes: string[] = orderResponse.data.scheduleTypesOrder || [];

          // 저장된 순서대로 정렬하되, 새로 추가된 타입은 뒤에 추가
          const existingTypes = savedTypes.filter(type => uniqueTypes.includes(type));
          const newTypes = uniqueTypes.filter(type => !savedTypes.includes(type));
          const orderedTypes = [...existingTypes, ...newTypes];

          setScheduleTypes(orderedTypes);

          // 자동 판단: 현재 날짜 기준으로 스케줄 타입 설정
          const autoType = getAutoScheduleType(orderedTypes);
          setSelectedRealtimeType(autoType);

          // 서버에도 자동 판단된 타입으로 설정
          try {
            await scheduleAPI.setActiveRealtimeSchedule(autoType);
            // console.log(`자동 설정: ${autoType}`);
          } catch (error) {
            console.error('자동 스케줄 타입 설정 실패:', error);
          }
        } catch (error) {
          console.error('저장된 순서 불러오기 실패:', error);
          // 서버에서 순서를 불러오지 못한 경우 기본 순서 사용
          setScheduleTypes(uniqueTypes);
          // 자동 판단
          const autoType = getAutoScheduleType(uniqueTypes);
          setSelectedRealtimeType(autoType);
        }
      }
    } catch (error: any) {
      console.error('전체 스케줄 로드 실패:', error);
    }
  };

  // 공휴일 체크 함수
  const isHoliday = (date: Date): boolean => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 0-based
    const day = date.getDate();
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // 2025년 대한민국 공휴일 목록 (예시)
    const holidays = [
      '2025-01-01', // 신정
      '2025-01-28', '2025-01-29', '2025-01-30', // 설날
      '2025-03-01', // 삼일절
      '2025-05-05', // 어린이날
      '2025-05-06', // 어린이날 대체공휴일
      '2025-06-06', // 현충일
      '2025-08-15', // 광복절
      '2025-10-03', // 개천절
      '2025-10-05', '2025-10-06', '2025-10-07', // 추석
      '2025-10-09', // 한글날
      '2025-12-25', // 크리스마스
    ];

    return holidays.includes(dateStr);
  };

  // 현재 날짜 기준으로 스케줄 타입 자동 판단
  const getAutoScheduleType = (scheduleTypes: string[]): string => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0(일요일) ~ 6(토요일)

    // 공휴일 체크
    if (isHoliday(today)) {
      // '공휴일' 타입이 있으면 반환
      const holidayType = scheduleTypes.find(type =>
        type.includes('공휴일') || type.includes('휴일')
      );
      if (holidayType) return holidayType;

      // 공휴일 타입이 없으면 주말 타입으로 대체
      const weekendType = scheduleTypes.find(type => type.includes('주말'));
      if (weekendType) return weekendType;
    }

    // 주말 체크
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      // '주말' 타입이 있으면 반환, 없으면 첫 번째 타입
      return scheduleTypes.find(type => type.includes('주말')) || scheduleTypes[0];
    }

    // 평일
    return scheduleTypes.find(type => type.includes('평일')) || scheduleTypes[0];
  };

  // 실시간 스케줄 로드
  const loadRealtimeSchedules = async (scheduleType: string) => {
    try {
      const response = await scheduleAPI.getSchedules(scheduleType);
      setRealtimeSchedules(response.data || []);
    } catch (error: any) {
      console.error('실시간 스케줄 로드 실패:', error);
      setRealtimeSchedules([]);
    }
  };

  // 그리팅 데이터 로드
  const loadGreetings = async () => {
    try {
      const response = await greetingService.getGreetingList({ limit: 1000 });
      setGreetings(response.data || []);
    } catch (error: any) {
      console.error('그리팅 로드 실패:', error);
      setGreetings([]);
    }
  };

  // 선택된 날짜에 해당하는 그리팅 필터링
  const filterGreetingsByDate = (greetings: GreetingItem[], selectedDate: dayjs.Dayjs): GreetingItem[] => {
    return greetings.filter(greeting => {
      // startDate와 endDate가 모두 없으면 항상 표시
      if (!greeting.startDate && !greeting.endDate) {
        return true;
      }

      const start = greeting.startDate ? dayjs(greeting.startDate) : null;
      const end = greeting.endDate ? dayjs(greeting.endDate) : null;

      // 날짜 범위 체크 (시작일 <= 선택날짜 <= 종료일)
      if (start && end) {
        return selectedDate.isBetween(start, end, 'day', '[]');
      } else if (start) {
        return selectedDate.isAfter(start, 'day') || selectedDate.isSame(start, 'day');
      } else if (end) {
        return selectedDate.isBefore(end, 'day') || selectedDate.isSame(end, 'day');
      }

      return false;
    });
  };

  // "HH:mm:ss" 형식을 타임라인 백분율(0-100%)로 변환
  const timeStringToPercent = (timeString: string): number => {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    const totalSeconds = hours * 3600 + minutes * 60 + (seconds || 0);
    return (totalSeconds / 86400) * 100; // 86400 = 24시간의 총 초
  };

  // 현재 날짜에 표시할 그리팅
  const visibleGreetings = filterGreetingsByDate(greetings, selectedDate);

  useEffect(() => {
    loadAllSchedules();
    loadRealtimeSchedules(selectedRealtimeType);
    loadGreetings();
    loadContentItems();
  }, [refreshKey, selectedRealtimeType]);

  useEffect(() => {
    loadSchedules(activeTab);
  }, [activeTab]);

  const handleRefresh = () => {
    setSelectedDate(dayjs());
    setRefreshKey(prev => prev + 1);
  };

  // 실시간 스케줄 타입 변경
  const handleRealtimeTypeChange = async (type: string) => {
    setSelectedRealtimeType(type);
    try {
      await scheduleAPI.setActiveRealtimeSchedule(type);
      getMessage().success(`실시간 스케줄이 "${type}"로 설정되었습니다.`);
    } catch (error: any) {
      getMessage().error(error.response?.data?.message || '실시간 스케줄 설정에 실패했습니다.');
    }
  };

  // 스케줄설정하기 버튼 클릭
  const handleSetActiveSchedule = async (type: string) => {
    try {
      await scheduleAPI.setActiveRealtimeSchedule(type);
      setSelectedRealtimeType(type);
      getMessage().success(`실시간 스케줄이 "${type}"로 설정되었습니다.`);
    } catch (error: any) {
      getMessage().error(error.response?.data?.message || '실시간 스케줄 설정에 실패했습니다.');
    }
  };

  const timeToPercent = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const dayMinutes = 24 * 60;
    return (totalMinutes / dayMinutes) * 100;
  };

  const getColorByTimeSlot = (timeSlotName: string) => {
    if (timeSlotName.includes('출근') || timeSlotName.includes('아침')) return '#FF746C';
    if (timeSlotName.includes('점심') || timeSlotName.includes('주간')) return '#F8CE51';
    if (timeSlotName.includes('퇴근') || timeSlotName.includes('저녁')) return '#5FD065';
    if (timeSlotName.includes('밤')) return '#7096FF';
    if (timeSlotName.includes('하루종일')) return '#FA8E54FF';
    return '#666';
  };

  // 비노출 시 배경색만 투명하게 (20% opacity)
  const getBackgroundColorByTimeSlot = (timeSlotName: string, isActive: boolean) => {
    const color = getColorByTimeSlot(timeSlotName);
    if (isActive) return color;

    // HEX를 RGB로 변환하여 rgba 적용
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.2)`;
  };

  const handleTimelineClick = (schedule: Schedule) => {
    navigate(`/schedule-management/detail?id=${schedule.id}`);
  };

  const openModal = (schedule?: Schedule) => {
    if (schedule) {
      setEditingSchedule(schedule);
      form.setFieldsValue({
        scheduleType: schedule.scheduleType,
        timeSlotName: schedule.timeSlotName,
        startTime: dayjs(schedule.startTime, 'HH:mm:ss'),
        endTime: dayjs(schedule.endTime, 'HH:mm:ss'),
        contents: schedule.contents?.map(content => ({
          contentItemId: content.contentItemId,
          duration: content.duration
        })) || []
      });
    } else {
      setEditingSchedule(null);
      form.resetFields();
      form.setFieldsValue({
        scheduleType: activeTab,
        contents: []
      });
    }
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const handleAfterClose = () => {
    setEditingSchedule(null);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const data = {
        scheduleType: values.scheduleType,
        timeSlotName: values.timeSlotName,
        startTime: values.startTime.format('HH:mm'),
        endTime: values.endTime.format('HH:mm'),
        isActive: true
      };

      const contents = (values.contents || []).map((content: any, index: number) => ({
        contentItemId: content.contentItemId,
        displayOrder: index + 1,
        duration: content.duration || '00:00'
      }));

      if (editingSchedule) {
        await scheduleAPI.updateSchedule(editingSchedule.id, data);
        await scheduleAPI.updateScheduleContents(editingSchedule.id, contents);
        getMessage().success('성공적으로 저장되었습니다.');
      } else {
        await scheduleAPI.createSchedule({ ...data, contents });
        getMessage().success('성공적으로 추가되었습니다.');
      }

      closeModal();
      loadAllSchedules();
      loadSchedules(activeTab);
      loadRealtimeSchedules(selectedRealtimeType);
    } catch (error: any) {
      if (error.errorFields) {
        getMessage().error('필수 항목을 입력해주세요.');
      } else {
        getMessage().error(error.response?.data?.message || '스케줄 저장에 실패했습니다.');
      }
    }
  };

  const handleDelete = (schedule: Schedule) => {
    showConfirmModal({
      title: '알림',
      content: '삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?',
      okText: '확인',
      cancelText: '취소',
      onConfirm: async () => {
        try {
          await scheduleAPI.deleteSchedule(schedule.id);
          getMessage().success('삭제가 완료되었습니다.');
          loadAllSchedules();
          loadSchedules(activeTab);
          loadRealtimeSchedules(selectedRealtimeType);
        } catch (error: any) {
          getMessage().error(error.response?.data?.message || '스케줄 삭제에 실패했습니다.');
        }
      }
    });
  };

  const renderScheduleCards = (scheduleList: Schedule[]) => {
    if (scheduleList.length === 0) {
      return (
        <div style={{ width: '100%', textAlign: 'center', padding: '40px', color: '#a6a6a6' }}>
          <Text>스케줄이 없습니다.</Text>
        </div>
      );
    }

    return scheduleList.map((schedule) => (
      <Card
        key={schedule.id}
        size="small"
        style={{
          flex: 1,
          minWidth: '200px',
          border: `2px solid ${getColorByTimeSlot(schedule.timeSlotName)}`,
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          opacity: schedule.isActive ? 1 : 0.6
        }}
        styles={{ body: { padding: '16px' } }}
        onClick={() => handleTimelineClick(schedule)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: getColorByTimeSlot(schedule.timeSlotName)
            }} />
            <Text strong style={{ fontSize: '16px' }}>
              {schedule.timeSlotName}
            </Text>
          </div>
          {isDeveloperAdmin && (
            <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  openModal(schedule);
                }}
              />
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(schedule);
                }}
              />
            </div>
          )}
        </div>

        <div style={{ marginBottom: '12px' }}>
          <Text strong>{schedule.scheduleType}</Text>
          <Text style={{ marginLeft: '8px', color: '#a6a6a6' }}>
            {schedule.startTime.substring(0, 5)} - {schedule.endTime.substring(0, 5)}
          </Text>
        </div>

        <div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px'
          }}>
            {schedule.contents.map((content, index) => (
              <Tag
                key={index}
                style={{
                  margin: '2px',
                  fontSize: '12px',
                  backgroundColor: '#2a2a2a',
                  border: '1px solid #404040',
                  color: '#ffffff'
                }}
              >
                {content.contentItem?.contentTitle || `ContentItem #${content.contentItemId}`}
              </Tag>
            ))}
            {schedule.contents.length === 0 && (
              <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>콘텐츠 없음</Text>
            )}
          </div>
        </div>
      </Card>
    ));
  };

  const TimelineBar = ({ schedules: timelineSchedules, showTimeLabels = false, showCurrentTime = false }: { schedules: Schedule[], showTimeLabels?: boolean, showCurrentTime?: boolean }) => {
    const [hoveredSection, setHoveredSection] = useState<number | null>(null);
    const [hoveredGreeting, setHoveredGreeting] = useState<number | null>(null);
    const [currentTime, setCurrentTime] = useState(dayjs());
    const [resizing, setResizing] = useState<{
      scheduleId: number;
      edge: 'start' | 'end';
      originalTime: string;
      originalPercent: number;
    } | null>(null);
    const [tempSchedules, setTempSchedules] = useState<Schedule[]>(timelineSchedules);
    const timelineRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
      setTempSchedules(timelineSchedules);
    }, [timelineSchedules]);

    useEffect(() => {
      if (!showCurrentTime) return;

      const updateCurrentTime = () => {
        setCurrentTime(dayjs());
      };

      updateCurrentTime();
      const interval = setInterval(updateCurrentTime, 60000); // 1분마다 업데이트

      return () => clearInterval(interval);
    }, [showCurrentTime]);

    const currentTimePercent = showCurrentTime
      ? timeToPercent(currentTime.format('HH:mm'))
      : 0;

    // 현재 시간이 스케줄 시간 범위 안에 있는지 확인
    const isScheduleActive = (schedule: Schedule): boolean => {
      if (!showCurrentTime) return schedule.isActive;

      const currentMinutes = currentTime.hour() * 60 + currentTime.minute();
      const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
      const [endHour, endMinute] = schedule.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;

      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    };

    // 픽셀 위치를 시간 문자열로 변환
    const percentToTime = (percent: number): string => {
      const totalMinutes = Math.round((percent / 100) * 24 * 60);
      const hours = Math.floor(totalMinutes / 60) % 24;
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
    };

    // 리사이즈 시작
    const handleResizeStart = (e: React.MouseEvent, scheduleId: number, edge: 'start' | 'end') => {
      if (!isDeveloperAdmin) return;
      e.stopPropagation();

      const schedule = tempSchedules.find(s => s.id === scheduleId);
      if (!schedule) return;

      const originalTime = edge === 'start' ? schedule.startTime : schedule.endTime;
      const originalPercent = timeToPercent(originalTime);

      setResizing({
        scheduleId,
        edge,
        originalTime,
        originalPercent
      });
    };

    // 리사이즈 중
    const handleResizeMove = React.useCallback((e: MouseEvent) => {
      if (!resizing || !timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      let percent = Math.max(0, Math.min(100, (x / rect.width) * 100));

      setTempSchedules(prev => {
        const currentSchedule = prev.find(s => s.id === resizing.scheduleId);
        if (!currentSchedule) return prev;

        // 시간순으로 정렬된 스케줄 목록
        const sortedSchedules = [...prev].sort((a, b) => {
          const aStart = timeToPercent(a.startTime);
          const bStart = timeToPercent(b.startTime);
          return aStart - bStart;
        });

        const currentIndex = sortedSchedules.findIndex(s => s.id === resizing.scheduleId);
        const prevSchedule = currentIndex > 0 ? sortedSchedules[currentIndex - 1] : null;
        const nextSchedule = currentIndex < sortedSchedules.length - 1 ? sortedSchedules[currentIndex + 1] : null;

        if (resizing.edge === 'start') {
          // 시작 시간 변경
          const endPercent = timeToPercent(currentSchedule.endTime);
          const minPercent = 2; // 최소 2% 간격 (약 30분)

          // 이전 스케줄과 겹치지 않도록 제한
          if (prevSchedule) {
            const prevEndPercent = timeToPercent(prevSchedule.endTime);
            percent = Math.max(percent, prevEndPercent);
          }

          // 현재 스케줄의 종료 시간보다 앞서야 함
          if (percent >= endPercent - minPercent) {
            percent = endPercent - minPercent;
          }

          const newTime = percentToTime(percent);
          return prev.map(schedule =>
            schedule.id === resizing.scheduleId
              ? { ...schedule, startTime: newTime }
              : schedule
          );
        } else {
          // 종료 시간 변경
          const startPercent = timeToPercent(currentSchedule.startTime);
          const minPercent = 2; // 최소 2% 간격

          // 다음 스케줄과 겹치지 않도록 제한
          if (nextSchedule) {
            const nextStartPercent = timeToPercent(nextSchedule.startTime);
            percent = Math.min(percent, nextStartPercent);
          }

          // 현재 스케줄의 시작 시간보다 뒤여야 함
          if (percent <= startPercent + minPercent) {
            percent = startPercent + minPercent;
          }

          const newTime = percentToTime(percent);
          return prev.map(schedule =>
            schedule.id === resizing.scheduleId
              ? { ...schedule, endTime: newTime }
              : schedule
          );
        }
      });
    }, [resizing]);

    // 리사이즈 종료
    const handleResizeEnd = React.useCallback(async () => {
      if (!resizing) return;

      const updatedSchedule = tempSchedules.find(s => s.id === resizing.scheduleId);
      if (!updatedSchedule) {
        setResizing(null);
        return;
      }

      try {
        // 서버에 저장
        await scheduleAPI.updateSchedule(resizing.scheduleId, {
          startTime: updatedSchedule.startTime.substring(0, 5),
          endTime: updatedSchedule.endTime.substring(0, 5)
        });

        getMessage().success('성공적으로 저장되었습니다.');

        // 데이터 새로고침
        loadSchedules(activeTab);
        loadRealtimeSchedules(selectedRealtimeType);
      } catch (error: any) {
        getMessage().error(error.response?.data?.message || '스케줄 시간 수정에 실패했습니다.');
        // 실패 시 원래 값으로 복원
        setTempSchedules(timelineSchedules);
      } finally {
        setResizing(null);
      }
    }, [resizing, tempSchedules, timelineSchedules, activeTab, selectedRealtimeType]);

    // 마우스 이벤트 리스너 등록
    React.useEffect(() => {
      if (!resizing) return;

      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);

      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }, [resizing, handleResizeMove, handleResizeEnd]);

    return (
      <div style={{ marginBottom: showTimeLabels ? '32px' : '24px' }}>
        <div
          ref={timelineRef}
          style={{
            position: 'relative',
            height: '68px',
            borderRadius: '4px',
            overflow: 'visible',
            cursor: resizing ? 'col-resize' : 'default'
          }}
        >
          <div style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backgroundColor: '#3A3A3AFF',
            border: '1px solid #484848FF',
            borderRadius: '4px'
          }} />

          {/* 세로 구분선들 */}
          <div style={{
            position: 'absolute',
            left: '25%',
            top: 0,
            width: '1px',
            height: '100%',
            backgroundColor: '#484848FF',
            zIndex: 2
          }} />
          <div style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            width: '1px',
            height: '100%',
            backgroundColor: '#484848FF',
            zIndex: 2
          }} />
          <div style={{
            position: 'absolute',
            left: '75%',
            top: 0,
            width: '1px',
            height: '100%',
            backgroundColor: '#484848FF',
            zIndex: 2
          }} />

          {tempSchedules.map((schedule) => {
            const left = timeToPercent(schedule.startTime);
            const width = timeToPercent(schedule.endTime) - left;
            const isResizingThis = resizing?.scheduleId === schedule.id;
            const isActive = isScheduleActive(schedule);

            return (
              <div
                key={schedule.id}
                style={{
                  position: 'absolute',
                  left: `${left}%`,
                  width: `${width}%`,
                  height: '100%',
                  backgroundColor: getBackgroundColorByTimeSlot(schedule.timeSlotName, isActive),
                  border: `1px solid ${getColorByTimeSlot(schedule.timeSlotName)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: showCurrentTime && isActive ? 'black' : 'white',
                  fontWeight: 'bold',
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                  transition: isResizingThis ? 'none' : 'background-color 0.2s, color 0.2s',
                  boxShadow: isResizingThis ? '0 0 0 2px #ffffff' : 'none',
                  zIndex: hoveredSection === schedule.id || isResizingThis ? 35 : 5
                }}
                onMouseEnter={() => !resizing && setHoveredSection(schedule.id)}
                onMouseLeave={() => !resizing && setHoveredSection(null)}
              >
                {/* 왼쪽 리사이즈 핸들 */}
                {isDeveloperAdmin && !showCurrentTime && (
                  <div
                    onMouseDown={(e) => handleResizeStart(e, schedule.id, 'start')}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '10px',
                      cursor: 'col-resize',
                      backgroundColor: hoveredSection === schedule.id || resizing?.scheduleId === schedule.id
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'transparent',
                      transition: 'background-color 0.2s',
                      zIndex: 2
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '3px',
                      height: '20px',
                      backgroundColor: hoveredSection === schedule.id || resizing?.scheduleId === schedule.id
                        ? '#ffffff'
                        : 'transparent',
                      borderRadius: '2px',
                      transition: 'background-color 0.2s'
                    }} />
                  </div>
                )}

                {schedule.timeSlotName}

                {/* 오른쪽 리사이즈 핸들 */}
                {isDeveloperAdmin && !showCurrentTime && (
                  <div
                    onMouseDown={(e) => handleResizeStart(e, schedule.id, 'end')}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      bottom: 0,
                      width: '10px',
                      cursor: 'col-resize',
                      backgroundColor: hoveredSection === schedule.id || resizing?.scheduleId === schedule.id
                        ? 'rgba(255, 255, 255, 0.3)'
                        : 'transparent',
                      transition: 'background-color 0.2s',
                      zIndex: 2
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '3px',
                      height: '20px',
                      backgroundColor: hoveredSection === schedule.id || resizing?.scheduleId === schedule.id
                        ? '#ffffff'
                        : 'transparent',
                      borderRadius: '2px',
                      transition: 'background-color 0.2s'
                    }} />
                  </div>
                )}

                {(hoveredSection === schedule.id || isResizingThis) && (
                  <div style={{
                    position: 'absolute',
                    top: '-48px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 30,
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      backgroundColor: isResizingThis ? '#1890ff' : '#3F3F3FFF',
                      color: '#ffffff',
                      padding: '8px 14px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                      transition: 'background-color 0.2s'
                    }}>
                      {schedule.timeSlotName} {schedule.startTime.substring(0, 5)} ~ {schedule.endTime.substring(0, 5)}
                      {isResizingThis && (
                        <div style={{ fontSize: '11px', marginTop: '2px', opacity: 0.9 }}>
                          드래그하여 시간 조절 중
                        </div>
                      )}
                    </div>
                    <div style={{
                      position: 'absolute',
                      bottom: '-6px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: `6px solid ${isResizingThis ? '#1890ff' : '#3F3F3FFF'}`
                    }} />
                  </div>
                )}
              </div>
            );
          })}

          {/* 그리팅 오버레이 레이어 */}
          {showCurrentTime && visibleGreetings
            .filter(greeting => greeting.exposureType === 'show') // 노출 설정된 것만 표시
            .map((greeting) => {
              // 상시 노출: startTime 또는 endTime이 없으면 전체 범위 (0% ~ 100%)
              const startPercent = greeting.startTime ? timeStringToPercent(greeting.startTime) : 0;
              const endPercent = greeting.endTime ? timeStringToPercent(greeting.endTime) : 100;

              // 현재 시간이 그리팅 시간 범위 안에 있는지 확인
              let isActive = true; // 기본값: 상시 노출은 항상 활성
              if (greeting.startTime && greeting.endTime) {
                const currentMinutes = currentTime.hour() * 60 + currentTime.minute();
                const [startHour, startMinute] = greeting.startTime.split(':').map(Number);
                const [endHour, endMinute] = greeting.endTime.split(':').map(Number);
                const startMinutes = startHour * 60 + startMinute;
                const endMinutes = endHour * 60 + endMinute;
                isActive = currentMinutes >= startMinutes && currentMinutes <= endMinutes;
              }

              // 배경색 설정 (비노출 시 배경만 투명)
              const greetingBgColor = isActive ? '#8400FF' : 'rgba(132, 0, 255, 0.3)';

              return (
                <div
                  key={`greeting-${greeting.id}`}
                  style={{
                    position: 'absolute',
                    left: `${startPercent}%`,
                    width: `${endPercent - startPercent}%`,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '34px',
                    zIndex: hoveredGreeting === greeting.id ? 35 : 10,
                    borderRadius: '0px',
                    border: '1px solid #8400FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    color: 'white',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    backgroundColor: greetingBgColor,
                    backgroundImage: isActive
                      ? 'repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(255, 255, 255, 0.2) 6px, rgba(255, 255, 255, 0.3) 7px)'
                      : 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={() => setHoveredGreeting(greeting.id)}
                  onMouseLeave={() => setHoveredGreeting(null)}
                >
                  그리팅

                  {hoveredGreeting === greeting.id && (
                    <div style={{
                      position: 'absolute',
                      top: '-48px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 30,
                      pointerEvents: 'none'
                    }}>
                      <div style={{
                        backgroundColor: '#3F3F3FFF',
                        color: '#ffffff',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        transition: 'background-color 0.2s'
                      }}>
                        {greeting.title} {greeting.startTime && greeting.endTime
                          ? `${greeting.startTime.substring(0, 5)} ~ ${greeting.endTime.substring(0, 5)}`
                          : '상시 노출'}
                      </div>
                      <div style={{
                        position: 'absolute',
                        bottom: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '6px solid transparent',
                        borderRight: '6px solid transparent',
                        borderTop: '6px solid #3F3F3FFF'
                      }} />
                    </div>
                  )}
                </div>
              );
            })
          }

          {showCurrentTime && (
            <>
              <div style={{
                position: 'absolute',
                left: `${currentTimePercent}%`,
                top: '-16px',
                transform: 'translateX(-50%)',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: '#44FFEC',
                zIndex: 25
              }} />
              <div style={{
                position: 'absolute',
                left: `${currentTimePercent}%`,
                top: '-10px',
                bottom: '-10px',
                width: '2px',
                backgroundColor: '#44FFEC',
                transform: 'translateX(-50%)',
                zIndex: 25
              }} />
            </>
          )}
        </div>

        {showTimeLabels && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            paddingLeft: '2px',
            paddingRight: '2px'
          }}>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>00:00</Text>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>06:00</Text>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>12:00</Text>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>18:00</Text>
            <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>00:00</Text>
          </div>
        )}
      </div>
    );
  };

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: '스케쥴 관리' }
          ]}
          style={{ marginBottom: '8px' }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 32px 0' }}>
          <Title level={2} style={{ margin: '0', color: '#ffffff' }}>
            스케쥴 관리
          </Title>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {isDeveloperAdmin && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal()}
              >
                스케줄 추가
              </Button>
            )}
            {!isDeveloperAdmin && (
              <>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
                <Text style={{ color: '#a6a6a6', fontSize: '14px' }}>
                  스케쥴 변경 필요 시 HNINE 문의 바랍니다. 02-521-0518
                </Text>
              </>
            )}
          </div>
        </div>

        <Card title="실시간 스케쥴 현황" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              선택된 스케쥴 타입:
              <Select
                value={selectedRealtimeType}
                onChange={handleRealtimeTypeChange}
                style={{ width: 120 }}
                options={scheduleTypes.map(type => ({ label: type, value: type }))}
              />
              <Text style={{ color: '#ffffff', fontSize: '14px' }}>
                {selectedDate.format('YYYY년 MM월 DD일 HH시 mm분 ss초')}
              </Text>
              <Button icon={<ReloadOutlined />} onClick={handleRefresh} />
            </div>
          </div>

          <TimelineBar key={refreshKey} schedules={realtimeSchedules} showTimeLabels={true} showCurrentTime={true} />
        </Card>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            ...scheduleTypes.map(type => ({
              key: type,
              label: type,
              children: (
                <div>
                  <TimelineBar schedules={schedules} showTimeLabels={true} />
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    {renderScheduleCards(schedules)}
                  </div>
                  {isDeveloperAdmin && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                      <Button
                        type={selectedRealtimeType === type ? 'primary' : 'default'}
                        onClick={() => handleSetActiveSchedule(type)}
                        disabled={selectedRealtimeType === type}
                      >
                        {selectedRealtimeType === type ? '현재 실시간 스케줄' : '실시간 스케줄로 설정하기'}
                      </Button>
                    </div>
                  )}
                </div>
              )
            })),
            // 콘텐츠 관리 탭은 개발자 관리자만 표시
            ...(isDeveloperAdmin ? [{
              key: 'content-management',
              label: '콘텐츠 관리',
              children: <ContentItemManagement />
            }] : [])
          ]}
          style={{
            marginBottom: '24px',
          }}
        />

        {modalVisible && (
          <Modal
            title={editingSchedule ? '스케줄 수정' : '스케줄 추가'}
            open={modalVisible}
            onOk={handleSave}
            onCancel={closeModal}
            afterClose={handleAfterClose}
            okText="저장"
            cancelText="취소"
            width={700}
            styles={{
              mask: {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)'
              }
            }}
            closeIcon={<span style={{ color: '#ffffff' }}>✕</span>}
          >
            <Form
              form={form}
              layout="vertical"
            >
              <Form.Item
                label="스케줄 타입"
                name="scheduleType"
                rules={[{ required: true, message: '스케줄 타입을 입력해주세요.' }]}
              >
                <AutoComplete
                  options={scheduleTypes.map(type => ({ value: type }))}
                  placeholder="스케줄 타입을 입력하거나 선택하세요 (예: 평일, 주말, 공휴일)"
                  filterOption={(inputValue, option) =>
                    option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                  }
                />
              </Form.Item>

              <Form.Item
                label="시간대 명칭"
                name="timeSlotName"
                rules={[{ required: true, message: '시간대 명칭을 입력해주세요.' }]}
              >
                <Input placeholder="예: 출근 시간, 점심 시간" />
              </Form.Item>

              <Form.Item
                label="시작 시간"
                name="startTime"
                rules={[{ required: true, message: '시작 시간을 선택해주세요.' }]}
              >
                <TimePicker
                  format="HH:mm"
                  style={{ width: '100%' }}
                  placeholder="시작 시간 선택"
                />
              </Form.Item>

              <Form.Item
                label="종료 시간"
                name="endTime"
                rules={[{ required: true, message: '종료 시간을 선택해주세요.' }]}
              >
                <TimePicker
                  format="HH:mm"
                  style={{ width: '100%' }}
                  placeholder="종료 시간 선택"
                />
              </Form.Item>

              <Divider />

              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '14px' }}>시퀀스 관리</Text>
                <Text style={{ color: '#a6a6a6', fontSize: '12px', marginLeft: '8px' }}>
                  (순서대로 재생됩니다)
                </Text>
              </div>

              <Form.List name="contents">
                {(fields, { add, remove, move }) => {
                  const handleContentDragEnd = (event: DragEndEvent) => {
                    const { active, over } = event;
                    if (over && active.id !== over.id) {
                      const oldIndex = fields.findIndex(field => `content-${field.key}` === active.id);
                      const newIndex = fields.findIndex(field => `content-${field.key}` === over.id);
                      move(oldIndex, newIndex);
                    }
                  };

                  return (
                    <>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleContentDragEnd}
                      >
                        <SortableContext
                          items={fields.map(field => `content-${field.key}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          {fields.map(({ key, name, ...restField }, index) => (
                            <DraggableContentItem key={key} id={`content-${key}`}>
                              <Text style={{ color: '#a6a6a6', minWidth: '24px' }}>
                                {String(index + 1).padStart(2, '0')}
                              </Text>
                              <Form.Item
                                {...restField}
                                name={[name, 'contentItemId']}
                                rules={[{ required: true, message: '콘텐츠를 선택하세요' }]}
                                style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}
                              >
                                <Select
                                  placeholder="콘텐츠 선택"
                                  showSearch
                                  optionFilterProp="label"
                                  options={contentItems.map(item => ({
                                    value: item.id,
                                    label: item.contentTitle,
                                    duration: item.duration
                                  }))}
                                  onChange={(value, option: any) => {
                                    // 선택한 콘텐츠의 duration을 자동으로 설정
                                    const currentContents = form.getFieldValue('contents') || [];
                                    currentContents[index] = {
                                      ...currentContents[index],
                                      duration: option.duration
                                    };
                                    form.setFieldsValue({ contents: currentContents });
                                  }}
                                />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, 'duration']}
                                rules={[
                                  { required: true, message: '재생 시간을 입력하세요' },
                                  { pattern: /^\d{2}:\d{2}$/, message: 'mm:ss 형식으로 입력하세요' }
                                ]}
                                style={{ marginBottom: 0, width: '100px' }}
                              >
                                <Input placeholder="00:15" maxLength={5} />
                              </Form.Item>
                              <MinusCircleOutlined
                                onClick={() => remove(name)}
                                style={{ color: '#ff4d4f', fontSize: '16px' }}
                              />
                            </DraggableContentItem>
                          ))}
                        </SortableContext>
                      </DndContext>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined />}
                          style={{ boxShadow: 'none' }}
                        >
                          콘텐츠 추가
                        </Button>
                      </Form.Item>
                    </>
                  );
                }}
              </Form.List>
            </Form>
          </Modal>
        )}
      </Space>
    </Spin>
  );
};

export default ScheduleManagement;
